import type { ReplayService, ReplayOptions, ReplayPreparation, ReplayReport } from "../ReplayService.js";
import type { Flow } from "./Flow.js";

/**
 * No prior lockfile exists. This run records the initial generation and
 * writes the lockfile. No patch detection or application happens — there's
 * nothing to compare against.
 */
export class FirstGenerationFlow implements Flow {
    constructor(private readonly service: ReplayService) {}

    async prepare(options?: ReplayOptions): Promise<ReplayPreparation> {
        if (options?.dryRun) {
            // Dry-run may be called on a repo with no commits yet (truly first-gen).
            // `git rev-parse HEAD` fails in that case; fall back to empty string.
            const headSha = await this.service.git
                .exec(["rev-parse", "HEAD"])
                .then((s) => s.trim())
                .catch(() => "");
            return {
                flow: "first-generation",
                previousGenerationSha: null,
                currentGenerationSha: headSha,
                _prepared: {
                    kind: "terminal",
                    flow: "first-generation",
                    preparedReport: firstGenerationReport(),
                },
            };
        }

        const commitOpts = options
            ? {
                  cliVersion: options.cliVersion ?? "unknown",
                  generatorVersions: options.generatorVersions ?? {},
              }
            : undefined;

        await this.service.committer.commitGeneration("Initial SDK generation", commitOpts);
        const genRecord = await this.service.committer.createGenerationRecord(commitOpts);
        // Phase 1: in-memory only. Disk save deferred to apply().
        this.service.lockManager.initializeInMemory(genRecord);

        return {
            flow: "first-generation",
            previousGenerationSha: null,
            currentGenerationSha: genRecord.commit_sha,
            _prepared: {
                kind: "active",
                flow: "first-generation",
                patchesToApply: [],
                newPatchIds: new Set(),
                detectorWarnings: [],
                detectorDegraded: [],
                revertedCount: 0,
                genSha: genRecord.commit_sha,
                optionsSnapshot: options,
            },
        };
    }

    async apply(_prep: ReplayPreparation): Promise<ReplayReport> {
        // Save the in-memory lockfile to disk. Matches today's behavior of
        // `lockManager.initialize(genRecord)` which internally calls save().
        // No replay commit in first-gen — the lockfile file lands untracked
        // and is committed by the next cycle's [fern-replay] commit.
        this.service.lockManager.setFernignorePatterns(this.service.readFernignorePatterns());
        this.service.lockManager.save();
        return firstGenerationReport();
    }
}

/**
 * Empty report shape returned by both the dry-run terminal and the active
 * apply path. Defined here (not on the service) because both this flow's
 * prepare and apply construct it identically.
 */
export function firstGenerationReport(): ReplayReport {
    return {
        flow: "first-generation",
        patchesDetected: 0,
        patchesApplied: 0,
        patchesWithConflicts: 0,
        patchesSkipped: 0,
        conflicts: [],
    };
}
